<template>
  <li
    :class="{
      hand: user.hand,
      teacher: user.role === 'teacher',
      inRoom: room.name !== MAIN_ROOM,
      selected: user.selected,
      invisible: !user.visible,
      randomSelected: user.isSelectedRandomly,
    }"
    :draggable="app.isTeacher() || (user === room.getLocalUser() && user.permissions.rooms) ? true : undefined"
    style="position: relative"
    @dragstart="app.isTeacher() || (user === room.getLocalUser() && user.permissions.rooms) ? startDrag($event, user) : undefined"
    @contextmenu.prevent.stop="onOpenUserMenu(user.id, $event)"
    @dblclick="clearAllStatusIndicators(user.id)"
    @click="app.isTeacher() ? selectUser($event, user) : undefined"
  >
    <span
      v-if="app.isTeacher() && user.role !== 'teacher'"
      :class="user.participationCount === 0 ? 'participation-count-zero' : 'participation-count-some'"
      title="Participation Count: Click to add; Shift+click to take away"
      style="position: absolute; left: -16px; top: 1px"
      @click.exact="app.updateParticipation(1, user.id)"
      @click.shift.exact="app.updateParticipation(-1, user.id)"
    >
      {{ user.participationCount }}
    </span>
    <span :style="{ visibility: user.hand ? 'visible' : 'hidden' }">{{ getHandIndex(user) }}✋</span>
    <span>{{ user.emoji }}</span>
    <span>{{ user.userStatus }}</span>

    <span :style="user === room.getLocalUser() ? 'font-weight: bold' : ''">
      <span
        style="margin-right: 5px"
        :style="{
          opacity: user.userStatus === '⏳' ? 0.4 : undefined,
          color: user.connectionQuality < 75 ? `hsl(${user.connectionQuality}, 100%, 50%)` : undefined,
        }"
        :class="{
          fadeBackground: user.justJoined,
          confettiUserName: user.confetti,
          'is-cloaked': user.cloaked,
        }"
      >
        {{ user.name }}
      </span>
    </span>

    <span v-show="user.permissions.audio || user.role === 'teacher'" style="margin-left: auto">
      <i
        class="bx bxs-microphone"
        :class="{
          inUse: user.isOnAudio,
          faded: !user.isOnAudio,
          pulse: user.isOnAudio && user.audioLevel > 0.05,
        }"
      />
    </span>
    <span v-show="user.permissions.video || user.role === 'teacher'">
      <i
        class="bx bxs-video"
        :class="{
          inUse: user.isOnCamera,
          faded: !user.isOnCamera,
        }"
      />
    </span>
    <span v-show="user.permissions.chat || user.role === 'teacher'">
      <i
        class="bx bxs-message-rounded-check"
        :class="{
          inUse: user.isTyping,
          faded: !user.isTyping,
        }"
      />
    </span>
    <span v-show="user.permissions.whiteboard || user.role === 'teacher'">
      <i
        class="bx bxs-edit-alt"
        :class="{
          inUse: user.isDrawing,
          faded: !user.isDrawing,
        }"
      />
    </span>
    <span v-show="user.permissions.screenshare || user.role === 'teacher'">
      <i
        class="bx bx-outline"
        :class="{
          inUse: user.isOnScreenShare,
          faded: !user.isOnScreenShare,
        }"
      />
    </span>
    <span v-show="user.permissions.topMenuControl">
      <i
        class="bx bx-crown"
        :class="{
          inUse: user.topMenuControl,
          faded: !user.topMenuControl,
        }"
      />
    </span>
    <span style="margin-right: 2px">
      <span v-show="showFeedback(room.getLocalUserId(), user.id)">
        <span v-html="formatUserFeedback(user)"></span>
      </span>
    </span>
  </li>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { Room } from '../models/Room';
import { User } from '../models/User';
import { MAIN_ROOM } from '../types';
import { getApp } from '../models/App';

defineProps<{
  room: Room;
  user: User;
  onOpenUserMenu: (userId: string, event: MouseEvent) => void;
}>();

const app = getApp();

const startDrag = (ev: DragEvent, user: User) => {
  if (ev.dataTransfer) {
    ev.dataTransfer.dropEffect = 'move';
    ev.dataTransfer.effectAllowed = 'move';

    // Make sure that dragged user is selected
    if (!user.selected) {
      selectUser(ev, user);
    }

    // Students cant select users. Thats why we use "|| user.id" as fallback
    const users =
      User.all()
        .filter((u) => u.selected)
        .map((u) => u.id)
        .join(',') || user.id;
    ev.dataTransfer.setData('text/plain', users);
  }
};

// Being able to clear out all status indicators upon double clicking a user name
const clearAllStatusIndicators = (id: string) => {
  // Only teachers and can clear users, and only users can clear themselves
  if (!app.isTeacher() && app.connection.id !== id) return;

  if (app.connection.id === id) {
    app.feedback = '';
    app.userStatus = '';
    app.emoji = '';
  } else {
    app.setUserFeedback(id, '');
    app.setUserEmoji(id, '');
    app.setUserStatus(id, '');
  }
};

const sortedUsersWithRaisedHands = computed(() =>
  User.all()
    .filter((user) => user.hand && user.visible)
    .sort((a, b) => a.handTime - b.handTime),
);

const getHandIndex = (user: User) => {
  return sortedUsersWithRaisedHands.value.indexOf(user) + 1;
};

const showFeedback = (localUserId: string, userId: string) => {
  return app.isTeacher() || app.feedbackVisibility || userId === localUserId;
};

const formatUserFeedback = (user: User) => {
  // Format the feedback display based on if it's a feedback rating value or not
  if (Number.isInteger(parseInt(user.feedback))) {
    return '⭐ ' + user.feedback;
  } else {
    return user.feedback;
  }
};

const selectUser = (ev: MouseEvent, user: User) => {
  // Prevent students from selecting people since they can't perform any actions with selected users anyway.
  if (!app.isTeacher()) return;

  if (!ev.ctrlKey) {
    User.all().forEach((u) => {
      u.selected = false;
    });
  }

  user.selected = ev.ctrlKey ? !user.selected : true;
};
</script>

<style scoped>
li {
  display: flex;
  align-items: center;
  gap: 2px;
}

li.me {
  font-weight: bold;
}

li:hover {
  background: rgba(0, 0, 0, 0.1);
}

li.hand {
  background: var(--color-accent-user);
}

li.teacher + :not(.teacher):not(.inRoom) {
  border-top: 1px solid black;
}

li.selected {
  background: var(--color-accent-icon);
}

li.invisible {
  background: gray;
}

li.randomSelected {
  background: #ffeb3b !important;
  animation: randomHighlight 0.3s ease-in-out;
  font-weight: bold;
  color: #333;
}

@keyframes randomHighlight {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 235, 59, 0.8);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(255, 235, 59, 0.8);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 235, 59, 0.8);
  }
}

.inUse {
  opacity: 1;
  border-radius: 100%;
}

.faded {
  opacity: 0.4;
}

.pulse {
  animation: pulse-effect 3s ease-in-out infinite;
}

@keyframes pulse-effect {
  0% {
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.5);
  }

  25% {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0);
  }

  50% {
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.5);
  }

  75% {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0);
  }

  100% {
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.5);
  }
}

.fadeBackground {
  animation: fadeBackground 5s ease-out;
  border-radius: 3px;
  padding-left: 5px;
  padding-right: 5px;
  left: -5px;
  position: relative;
}

@keyframes fadeBackground {
  from {
    background-color: red;
  }

  to {
    text-shadow:
      0 0 10px #fff,
      0 0 15px rgb(172, 54, 54),
      0 0 20px red,
      0 0 25px red;
    background-color: white;
  }
}

.participation-count-zero {
  cursor: pointer;
  background: red;
  border-radius: 50%;
  line-height: 10px;
  vertical-align: middle;
  text-align: center;
  border: 2px solid red;
  width: 14px;
  height: 14px;
  font-size: 11px;
  color: white;
  left: 5px;
  opacity: 0.4;
}

.participation-count-zero:hover {
  opacity: 1;
}

.participation-count-some {
  cursor: pointer;
  background: green;
  border-radius: 50%;
  line-height: 10px;
  vertical-align: middle;
  text-align: center;
  border: 2px solid green;
  width: 14px;
  height: 14px;
  font-size: 11px;
  color: white;
  left: 5px;
  opacity: 0.4;
}

.participation-count-some:hover {
  opacity: 1;
}

.confettiUserName {
  animation: confettiUserName 1s ease-in-out infinite;
  border-radius: 3px;
  padding-left: 5px;
  padding-right: 5px;
  position: relative;
}

@keyframes confettiUserName {
  0% {
    left: -10px;
    background-color: yellow;
    text-shadow: 0 0 10px #fff;
  }

  25% {
    left: 0px;
    text-shadow: 0 0 15px rgb(202, 182, 66);
  }

  50% {
    left: 10px;
    text-shadow: 0 0 20px yellow;
  }

  75% {
    left: 0px;
    text-shadow: 0 0 15px rgb(202, 182, 66);
  }

  100% {
    left: -10px;
    text-shadow: 0 0 10px #fff;
  }
}

.is-cloaked {
  font-style: italic;
  background: black;
  color: white;
  padding-left: 3px;
  padding-right: 3px;
}
</style>
